import { EventBarrier } from "@/components/common/event-barrier";
import { KeyCap } from "@/components/ui/help-banners/key-cap";
import { HorizontalResizeContainer } from "@/components/ui/resize-container";
import { usePhoneViewport } from "@/hooks/use-phone-viewport";
import { AnnotationEditorSurfaceContext } from "@/components/ui/annotations/annotation-editor/annotation-editor-surface-context";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import {
  FaroText,
  FaroTooltip,
  grey,
  HistoryUndoIcon,
  LocationPinIcon,
  Toolbar,
  ToolButton,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import {
  isIElementMarkupImagePoint,
  isIElementMarkupPolygon,
} from "@faro-lotv/ielement-types";
import { selectAncestor, selectIElement } from "@faro-lotv/project-source";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import {
  Dialog,
  Divider,
  Popover,
  Stack,
  SvgIcon,
  useMediaQuery,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import { AnnotationColorPalette } from "./annotation-color-palette";
import { AnnotationColor } from "./annotation-colors";
import { AnnotationCreationSheet } from "./annotation-creation-sheet";
import { ColorDot } from "./color-dot";
import {
  PEN_HOLE,
  PEN_NIB_PATH,
  PEN_SLIT_PATH,
} from "./annotation-shapes-cursors";
import {
  AnnotationShape,
  AnnotationShapeStyle,
  MIN_POLYGON_POINTS,
  OutlineStyle,
  StyleKind,
  useAnnotationShapesStore,
  useIsAnnotationShapesEnabled,
} from "./annotation-shapes-store";

const SHAPES = [
  { shape: AnnotationShape.point, label: "Point", icon: <LocationPinIcon /> },
  {
    shape: AnnotationShape.rectangle,
    label: "Rectangle",
    icon: <CropSquareIcon />,
  },
  {
    shape: AnnotationShape.polygon,
    label: "Polygon",
    icon: <PenToolIcon />,
  },
];

/** Line style options, with a small preview of the line for the toolbar, centered in the 24px icon box */
const OUTLINES = [
  { style: OutlineStyle.solid, label: "Solid", path: "M4 12h16" },
  {
    style: OutlineStyle.dashed,
    label: "Dashed",
    path: "M4 12h3m4.5 0h3m4.5 0h1",
  },
  {
    style: OutlineStyle.cloud,
    label: "Cloud",
    path: "M4 13.5a2.67 2.67 0 0 1 5.33 0a2.67 2.67 0 0 1 5.34 0a2.67 2.67 0 0 1 5.33 0",
  },
];

/**
 * @returns a pen tool icon, like the path tool of drawing apps, to tell the polygon is drawn corner by corner.
 * Placeholder until design provides the final icon
 */
function PenToolIcon(): JSX.Element {
  return (
    <SvgIcon>
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
      >
        <path d={PEN_NIB_PATH} />
        <path d={PEN_SLIT_PATH} />
        <circle {...PEN_HOLE} />
      </g>
    </SvgIcon>
  );
}

/** @returns a line icon previewing a line style */
function LineStyleIcon({ path }: { path: string }): JSX.Element {
  return (
    <SvgIcon>
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </SvgIcon>
  );
}

type ShapeButtonsProps = {
  /** Where the tooltips open, away from the toolbar */
  tooltipPlacement: "top" | "bottom";
};

/** @returns one toggle button per shape. Adding a shape only adds an entry to SHAPES */
function ShapeButtons({ tooltipPlacement }: ShapeButtonsProps): JSX.Element {
  const shape = useAnnotationShapesStore((s) => s.shape);
  const setShape = useAnnotationShapesStore((s) => s.setShape);

  return (
    <>
      {SHAPES.map(({ shape: value, label, icon }) => (
        <FaroTooltip key={value} title={label} placement={tooltipPlacement}>
          <ToolButton
            aria-label={`${label} annotation`}
            value={value}
            selected={shape === value}
            onClick={() => setShape(value)}
          >
            {icon}
          </ToolButton>
        </FaroTooltip>
      ))}
    </>
  );
}

/** @returns true when the bottom toolbar of the shapes is shown: while drawing, or while editing the style of an annotation */
export function useIsShapesBottomToolbarShown(): boolean {
  const isEnabled = useIsAnnotationShapesEnabled();
  const isActive = useAppSelector(selectActiveTool) === ToolName.annotation;
  const context = useAnnotationShapesStore((s) => s.context);
  const isEditingShape = useAnnotationShapesStore((s) => !!s.editedShape);
  return isEnabled && (isEditingShape || (isActive && !!context));
}

/**
 * @returns the bottom toolbar of the shapes, home of how a shape looks. Like manual blurring, while drawing:
 * the shape picker, the style of the next shape, and the undo of the last corner.
 * While editing a shape: only its style, since the shape itself can't be changed yet.
 */
export function AnnotationShapesBottomToolbar(): JSX.Element | null {
  const isShown = useIsShapesBottomToolbarShown();
  const draft = useAnnotationShapesStore((s) => s.draft);
  const shape = useAnnotationShapesStore((s) => s.shape);
  const undoDraftPoint = useAnnotationShapesStore((s) => s.undoDraftPoint);
  const editedShape = useAnnotationShapesStore((s) => s.editedShape);
  const setEditedStyle = useAnnotationShapesStore((s) => s.setEditedStyle);

  if (!isShown) return null;

  const isDrawing = !!draft && !draft.isClosed;
  const isPolygon = draft?.shape === AnnotationShape.polygon;

  return (
    <EventBarrier>
      <Toolbar
        aria-label="annotation shapes toolbar"
        direction="row"
        sx={{ alignItems: "center" }}
      >
        {editedShape ? (
          <StyleToolGroups
            color={editedShape.color}
            outline={
              editedShape.kind === "shape" ? editedShape.outline : undefined
            }
            onChange={setEditedStyle}
            placement="top"
          />
        ) : (
          <>
            <ToolGroup orientation="horizontal">
              <ShapeButtons tooltipPlacement="top" />
            </ToolGroup>
            <ToolbarSeparator />
            <NextStyleToolGroups shape={shape} placement="top" />
            {/* The polygon is finished on the canvas and the annotation is created from the panel, so no confirm here */}
            {isDrawing && isPolygon && (
              <ToolGroup orientation="horizontal">
                <FaroTooltip
                  title="Remove last corner (Backspace)"
                  placement="top"
                >
                  <ToolButton aria-label="undo corner" onClick={undoDraftPoint}>
                    <HistoryUndoIcon />
                  </ToolButton>
                </FaroTooltip>
              </ToolGroup>
            )}
          </>
        )}
      </Toolbar>
    </EventBarrier>
  );
}

/**
 * @returns a vertical line between the shape picker and the settings of the selected shape,
 * to tell the settings are contextual to the shape, not other shapes to pick
 */
function ToolbarSeparator(): JSX.Element {
  return (
    <Divider
      orientation="vertical"
      flexItem
      aria-hidden
      sx={{ borderColor: grey[700], my: 1, mx: 0.5 }}
    />
  );
}

type Placement = {
  /** Where tooltips and the color palette open, away from the toolbar */
  placement: "top" | "bottom";
};

type NextStyleToolGroupsProps = Placement & {
  /** Shape selected in the tool */
  shape: AnnotationShape;
};

/**
 * @returns the style of the next annotation, previewed while drawing: the color of the pin for points,
 * the color and line style for shapes. Each is kept as the last used one.
 */
function NextStyleToolGroups({
  shape,
  placement,
}: NextStyleToolGroupsProps): JSX.Element {
  const style = useAnnotationShapesStore((s) => s.style);
  const setStyle = useAnnotationShapesStore((s) => s.setStyle);
  const pointColor = useAnnotationShapesStore((s) => s.pointColor);
  const setPointColor = useAnnotationShapesStore((s) => s.setPointColor);

  if (shape === AnnotationShape.point) {
    return (
      <StyleToolGroups
        color={pointColor}
        onChange={({ color }) => color && setPointColor(color)}
        placement={placement}
      />
    );
  }
  return (
    <StyleToolGroups
      color={style.color}
      outline={style.outline}
      onChange={setStyle}
      placement={placement}
    />
  );
}

type StyleToolGroupsProps = Placement & {
  /** Color of the annotation, undefined for a point with the default pin */
  color?: AnnotationColor;

  /** Line style of a shape, undefined for a point, which has none */
  outline?: OutlineStyle;

  onChange(style: Partial<AnnotationShapeStyle>): void;
};

/** @returns the color, and for shapes the line style, of an annotation as toolbar buttons */
function StyleToolGroups({
  color,
  outline: selectedOutline,
  onChange,
  placement,
}: StyleToolGroupsProps): JSX.Element {
  const [colorAnchor, setColorAnchor] = useState<HTMLElement>();
  const isTop = placement === "top";

  return (
    <>
      <ToolGroup orientation="horizontal">
        <FaroTooltip title="Color" placement={placement}>
          <ToolButton
            aria-label="annotation color"
            selected={!!colorAnchor}
            onClick={(e) => setColorAnchor(e.currentTarget)}
          >
            <ColorDot color={color} />
          </ToolButton>
        </FaroTooltip>
      </ToolGroup>
      <Popover
        open={!!colorAnchor}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(undefined)}
        anchorOrigin={{
          vertical: isTop ? "top" : "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: isTop ? "bottom" : "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: { p: 1.5, my: isTop ? -1 : 1, backgroundColor: grey[900] },
          },
        }}
      >
        <AnnotationColorPalette
          color={color}
          onChange={(picked) => {
            onChange({ color: picked });
            setColorAnchor(undefined);
          }}
        />
      </Popover>
      {selectedOutline && (
        <ToolGroup orientation="horizontal">
          {OUTLINES.map(({ style: outline, label, path }) => (
            <FaroTooltip
              key={outline}
              title={`${label} line`}
              placement={placement}
            >
              <ToolButton
                aria-label={`${label} line`}
                value={outline}
                selected={selectedOutline === outline}
                onClick={() => onChange({ outline })}
              >
                <LineStyleIcon path={path} />
              </ToolButton>
            </FaroTooltip>
          ))}
        </ToolGroup>
      )}
    </>
  );
}

type AnnotationEditInPanelProps = {
  /** Id of the markup of the annotation being edited */
  markupId: GUID;

  /** The edit form */
  children: JSX.Element;
};

/**
 * Shows the edit form of an annotation in the panel, like creation, instead of a dialog over the canvas.
 * For a shape, its style is edited from the bottom toolbar while the form is shown, and kept only if the form is saved.
 *
 * @returns nothing, the form is rendered by the AnnotationCreationPanel
 */
export function AnnotationEditInPanel({
  markupId,
  children,
}: AnnotationEditInPanelProps): null {
  const styleKind = useAppSelector(selectStyleKind(markupId));
  const setPanelContent = useAnnotationShapesStore((s) => s.setPanelContent);

  useEffect(() => {
    setPanelContent(children, { title: "Edit annotation", styleKind });
  }, [children, styleKind, setPanelContent]);

  useEffect(() => {
    if (styleKind) {
      useAnnotationShapesStore
        .getState()
        .startEditingShape(markupId, styleKind);
    }
    useAnnotationShapesStore.setState({ isSheetMinimized: false });
    // Closing without saving reverts the style, a save already kept it
    return () => {
      const { finishEditingShape, setPanelContent: setContent } =
        useAnnotationShapesStore.getState();
      finishEditingShape(false);
      setContent(undefined);
    };
  }, [markupId, styleKind]);

  return null;
}

/**
 * @param markupId id of the markup of the annotation
 * @returns a selector of what the style of the annotation applies to: a point or a shape, undefined for annotations
 * without a style, like measurements
 */
function selectStyleKind(
  markupId: GUID,
): (state: RootState) => StyleKind | undefined {
  return (state) => {
    const markup = selectIElement(markupId)(state);
    if (!markup) return;
    const annotation = selectAncestor(
      markup,
      (el) => isIElementMarkupPolygon(el) || isIElementMarkupImagePoint(el),
    )(state);
    if (!annotation) return;
    return isIElementMarkupPolygon(annotation) &&
      annotation.points.length >= MIN_POLYGON_POINTS
      ? "shape"
      : "point";
  };
}

/**
 * @returns the edit form of an annotation, in the panel with annotation shapes, otherwise in the dialog over the canvas
 */
export function EditAnnotationSurface({
  markupId,
  children,
}: AnnotationEditInPanelProps): JSX.Element | null {
  const isEnabled = useIsAnnotationShapesEnabled();
  if (isEnabled) {
    return (
      <AnnotationEditInPanel markupId={markupId}>
        {children}
      </AnnotationEditInPanel>
    );
  }
  return (
    <Dialog
      open
      slotProps={{
        paper: {
          sx: {
            padding: 0,
            backgroundColor: "transparent",
            overflow: "hidden",
            boxShadow: "none",
          },
        },
      }}
    >
      {children}
    </Dialog>
  );
}

/** @returns the callback to keep the style of the shape being edited, when its annotation is saved */
export function useSaveEditedShape(): () => void {
  return useCallback(
    () => useAnnotationShapesStore.getState().finishEditingShape(true),
    [],
  );
}

/**
 * @returns where annotations are created: a resizable panel on the right of the canvas on desktop and tablets,
 * so the shape stays visible, or on phones a full screen sheet that can be minimized to look at the shape.
 * It follows the app surfaces: light today like the other side panels, and dark with the dark theme,
 * by switching the surface context below to the active color scheme.
 */
export function AnnotationCreationPanel(): JSX.Element | null {
  const content = useAnnotationShapesStore((s) => s.panelContent);
  const isPhone = usePhoneViewport();

  if (isPhone) {
    return (
      <AnnotationEditorSurfaceContext value={PANEL_SURFACE}>
        <AnnotationCreationSheet content={content} />
      </AnnotationEditorSurfaceContext>
    );
  }
  if (!content) return null;

  return (
    <AnnotationEditorSurfaceContext value={PANEL_SURFACE}>
      <HorizontalResizeContainer
        initialWidth={
          window.innerWidth < NARROW_SCREEN_PX
            ? PANEL_WIDTH.min
            : PANEL_WIDTH.initial
        }
        minWidth={PANEL_WIDTH.min}
        maxWidth={PANEL_WIDTH.max}
        handleSide="left"
        sx={{ height: "100%", backgroundColor: "background.paper" }}
      >
        <Stack
          aria-label="annotation creation panel"
          sx={{ flexGrow: 1, minWidth: 0, overflowY: "auto" }}
        >
          {content}
        </Stack>
      </HorizontalResizeContainer>
    </AnnotationEditorSurfaceContext>
  );
}

const PANEL_SURFACE = { dark: false, fillWidth: true };

/** Width of the creation panel, in pixels: starts like the project tree, and can be resized */
const PANEL_WIDTH = { initial: 360, min: 320, max: 560 };

/** Below this screen width, like tablets in portrait, the panel opens at its minimum width to leave room to the canvas */
const NARROW_SCREEN_PX = 1024;

/**
 * @returns the one-line hint for the next step of the annotation tool, or undefined to keep the default text.
 * Like the measurement guidance (SWEB-9035): only what matters now, keys as key caps, no keyboard hints on touch.
 */
export function useAnnotationShapesHelpText(): ReactNode {
  const isEnabled = useIsAnnotationShapesEnabled();
  const shape = useAnnotationShapesStore((s) => s.shape);
  const context = useAnnotationShapesStore((s) => s.context);
  const draft = useAnnotationShapesStore((s) => s.draft);
  const canClose = useAnnotationShapesStore((s) => s.canClosePolygon);
  const isFormOpen = useAnnotationShapesStore((s) => !!s.panelContent);
  const wasDiscarded = useAnnotationShapesStore((s) => s.wasDiscarded);
  const isTouch = useMediaQuery("(pointer: coarse)");
  if (!isEnabled || !context) return;

  const hint = getShapeStepHint({
    shape,
    corners: draft && !draft.isClosed ? draft.points.length : 0,
    canClose,
    isFormOpen,
    wasDiscarded,
    isTouch,
  });
  return (
    <FaroText variant="bodyL" dark>
      {hint}
    </FaroText>
  );
}

export type ShapeStep = {
  shape: AnnotationShape;

  /** Corners of the polygon being drawn, 0 if none */
  corners: number;

  /** True when the next click closes the polygon */
  canClose: boolean;

  /** True while the details of the annotation are filled in the panel */
  isFormOpen: boolean;

  /** True after an annotation was discarded, until the next one is started */
  wasDiscarded: boolean;

  /** True on touch devices, where keyboard shortcuts are not available */
  isTouch: boolean;
};

/** @returns the hint for the next action of the annotation being created */
export function getShapeStepHint({
  shape,
  corners,
  canClose,
  isFormOpen,
  wasDiscarded,
  isTouch,
}: ShapeStep): ReactNode {
  const action = isTouch ? "Tap" : "Click";
  const isPoint = shape === AnnotationShape.point;

  if (isFormOpen) {
    return `Add the details, then select Create · ${
      isPoint
        ? `${action} elsewhere to move the point`
        : "Draw again to replace the area"
    }`;
  }

  if (corners > 0 && shape === AnnotationShape.rectangle) {
    return isTouch ? (
      "Release to finish the area"
    ) : (
      <>
        Release to finish the area · <KeyCap>ESC</KeyCap> to cancel
      </>
    );
  }
  if (canClose) return `${action} to close the area`;
  if (corners > 0) {
    const finish =
      corners < MIN_POLYGON_POINTS
        ? `Keep ${isTouch ? "tapping" : "clicking"} to outline the area`
        : `${action} the starting point${isTouch ? "" : " or double-click"} to finish`;
    if (isTouch) return finish;
    return (
      <>
        {finish} · <KeyCap>BACKSPACE</KeyCap> to undo · <KeyCap>ESC</KeyCap> to
        cancel
      </>
    );
  }

  let start = `${action} to place the annotation`;
  if (shape === AnnotationShape.rectangle) {
    start = `${isTouch ? "Drag" : "Click and drag"} to draw the area`;
  } else if (shape === AnnotationShape.polygon) {
    start = `${action} to start outlining the area`;
  }
  if (!wasDiscarded) return start;
  return (
    <>
      Annotation discarded · {start}
      {!isTouch && (
        <>
          {" "}
          · <KeyCap>ESC</KeyCap> to exit
        </>
      )}
    </>
  );
}
